import { DateObject } from 'react-multi-date-picker';
import moment from 'moment';
import type { OpenDay } from 'core/visitRequestForm/types';
import { formatTimeRange } from 'core/utils/date';

/**
 * Resolve the open-day marker for a single calendar cell.
 *
 * Open days are weekday-recurring (the API has no per-date endpoint), so the
 * date's weekday is matched against the site's configured days. Closed days
 * carry no marker at all, and that includes bank holidays: the backend treats
 * one as closing the open window, so marking it open would be wrong.
 *
 * @param  date     the calendar cell
 * @param  openDays site open days, keyed by weekday name
 * @param  holidays bank holidays in MM-DD-YYYY
 * @return the opening-hours label, or null when the site is closed
 */
export const resolveDayMarker = (
    date: DateObject,
    openDays: OpenDay[],
    holidays: string[],
): string | null => {
    const weekday = date.format('dddd').toLowerCase();
    const openDay = openDays.find((day) => day.day?.toLowerCase() === weekday);

    if (!openDay || holidays.includes(date.format('MM-DD-YYYY'))) {
        return null;
    }

    // 24/7 sites are synthesised server-side as 07:00-07:00 on every day.
    if (openDay.startTime === openDay.endTime) {
        return 'Open 24 hours';
    }

    return `Open ${formatTimeRange(openDay.startTime, openDay.endTime)}`;
};

export const convertDateStringToRanges = (dateStrings: string[]) => {
    if (dateStrings.length > 0) {

        const sortedDates = dateStrings.sort((date1, date2) => (moment(date1, 'MM-DD-YYYY').isAfter(moment(date2, 'MM-DD-YYYY')) ? 1 : -1));

        const ranges = [];
        let start = new Date(moment(sortedDates[0], 'MM-DD-YYYY').format('YYYY-MM-DD'));
        let end = new Date(moment(sortedDates[0], 'MM-DD-YYYY').format('YYYY-MM-DD'));

        for (let i = 1; i < sortedDates.length; i++) {
            end = new Date(moment(sortedDates[i], 'MM-DD-YYYY').format('YYYY-MM-DD'));

            if (moment(sortedDates[i], 'MM-DD-YYYY').diff(moment(sortedDates[i - 1], 'MM-DD-YYYY'), 'days') > 1) {
                ranges.push([start, new Date(moment(sortedDates[i - 1], 'MM-DD-YYYY').format('YYYY-MM-DD'))]);
                start = end;
            }
        }
        ranges.push([start, end]);
        return ranges;
    }
    return [];
};

export const convertRangesToDateString = (dateArray: DateObject[]) => {
    const dates: string[] = [];
    dateArray.forEach((range) => {
        // Each entry is a [start, end] pair of DateObjects.
        // Use the DateObject's own toDate() rather than new Date(range[0]):
        // passing a DateObject (or a non-ISO string) to new Date() is parsed
        // leniently by Chrome but returns Invalid Date in Safari, which then
        // renders as the literal "Invalid date".
        // @ts-expect-error type error in npm package for date ranges
        const startObj = range[0];
        // @ts-expect-error type error in npm package for date ranges
        const endObj = range[1];
        if (!startObj) {
            return;
        }
        const start: Date = startObj.toDate();

        // Mid-selection: only the start of this range has been clicked, so
        // there's no end yet. Emit the "Invalid date" sentinel that
        // handleDatesSelect() guards on, so the parent skips the state update
        // and the calendar keeps its in-progress range until the second click
        // completes it. Collapsing this into a 1-day range instead makes the
        // calendar treat the second click as a brand-new range, so the end
        // date is never selected (two clicks become two single days).
        if (!endObj) {
            dates.push(moment(start).format('MM-DD-YYYY'), 'Invalid date');
            return;
        }
        const end: Date = endObj.toDate();

        dates.push(moment(start).format('MM-DD-YYYY'));

        // If the range is more than a day, add the intervening dates
        const dayAfterStart = new Date(+start);
        dayAfterStart.setDate(dayAfterStart.getDate() + 1);

        while (dayAfterStart.getTime() < end.getTime()) {
            dates.push(moment(dayAfterStart).format('MM-DD-YYYY'));
            dayAfterStart.setDate(dayAfterStart.getDate() + 1);
        }

        // If start and end dates are not the same, add end date
        if (start.getTime() !== end.getTime()) {
            dates.push(moment(end).format('MM-DD-YYYY'));
        }
    });
    // Sort the dates array. Parse with the explicit MM-DD-YYYY format —
    // new Date("MM-DD-YYYY") is Invalid Date in Safari, breaking the sort.
    dates.sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());
    return dates;
};

export const categoriseDatesByMonth = (dates: string[]) => {
    // Parse with the explicit MM-DD-YYYY format; new Date("MM-DD-YYYY")
    // returns Invalid Date in Safari (works in Chrome only).
    const dateObjects = dates.map(dateString => moment(dateString, 'MM-DD-YYYY').toDate());
    const monthYears = dateObjects.map(date => moment(date).format('MMMM YYYY'));
    const uniqueMonths = Array.from(new Set(monthYears));
    const categorisedDates = uniqueMonths.map(monthYear => {
        const datesInMonth = dateObjects.filter(date => moment(date).format('MMMM YYYY') === monthYear);
        return {
            monthYear,
            dates: datesInMonth,
        };
    });
    return categorisedDates;
};
